import React, { useState } from 'react';
import styled from 'styled-components';
import { TagAnalysisService } from '../services/tagAnalysisService';
import TagDetailModal from './TagDetailModal';
import { resolveTestCaseValue } from '../utils/dataTransform';

interface TagInfoButtonProps {
  workspaceId?: string;
  projectId?: string;
  testResults: any[];  // 現在表示中の結果
  apiClient: any;
  filteredResults?: any[];  // API形式のフィルタリングされた結果
  allProjectData?: any[];  // 全プロジェクトのテストデータ（キャッシュ）
  onDataCached?: (data: any[]) => void;  // データ取得後のコールバック
}

const ButtonContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const InfoButton = styled.button<{ isLoading?: boolean }>`
  padding: 10px 20px;
  background: ${props => props.isLoading ? '#ccc' : '#4caf50'};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: ${props => props.isLoading ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: ${props => props.isLoading ? '#ccc' : '#45a049'};
  }
  
  &:active {
    transform: ${props => props.isLoading ? 'none' : 'translateY(1px)'};
  }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'block' : 'none'};
  position: fixed;
  z-index: 10000;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
`;

const ModalContent = styled.div`
  background-color: #fefefe;
  margin: 5% auto;
  padding: 20px;
  border: 1px solid #888;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 10px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #aaa;
  
  &:hover {
    color: #000;
  }
`;

const TagTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
`;

const TagTableHeader = styled.thead`
  background: #f5f5f5;
`;

const TagTableRow = styled.tr`
  border-bottom: 1px solid #ddd;
  
  &:hover {
    background: #f9f9f9;
  }
`;

const TagTableHeaderCell = styled.th`
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #555;
`;

const TagTableCell = styled.td<{ isClickable?: boolean }>`
  padding: 10px 12px;
  cursor: ${props => props.isClickable ? 'pointer' : 'default'};
  
  &:hover {
    color: ${props => props.isClickable ? '#1976d2' : 'inherit'};
    text-decoration: ${props => props.isClickable ? 'underline' : 'none'};
  }
`;

const TagTypeSection = styled.div`
  margin-bottom: 30px;
`;

const TagTypeTitle = styled.h3`
  color: #444;
  margin-bottom: 10px;
  padding: 5px 10px;
  background: #f8f8f8;
  border-left: 4px solid #4caf50;
`;

const NGRateBadge = styled.span<{ rate: number }>`
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 500;
  color: white;
  background: ${props => 
    props.rate >= 0.7 ? '#f44336' :
    props.rate >= 0.3 ? '#ff9800' :
    '#4caf50'
  };
`;

const SummarySection = styled.div`
  background: #f0f7ff;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const SummaryItem = styled.div`
  display: inline-block;
  margin-right: 30px;
  margin-bottom: 10px;
`;

const SummaryLabel = styled.span`
  font-weight: 600;
  color: #666;
  margin-right: 8px;
`;

const SummaryValue = styled.span`
  color: #333;
  font-size: 16px;
`;

const TagInfoButton: React.FC<TagInfoButtonProps> = ({
  workspaceId,
  projectId,
  testResults,
  apiClient,
  filteredResults,
  allProjectData,
  onDataCached
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [tagStatistics, setTagStatistics] = useState<any>(null);
  const [allTestResults, setAllTestResults] = useState<any[]>([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTag, setSelectedTag] = useState<{ name: string; type: string } | null>(null);

  const handleClick = async () => {
    
    if (!workspaceId || !projectId) {
      alert('ワークスペースIDとプロジェクトIDが必要です');
      return;
    }

    setIsLoading(true);
    
    try {
      let allResults: any[];
      
      // キャッシュされたデータがあればそれを使用
      if (allProjectData && allProjectData.length > 0) {
        console.log('キャッシュされたデータを使用:', allProjectData.length, '件');
        console.warn('⚠️ デバッグ: キャッシュデータの最初の要素:', allProjectData[0]);
        console.error('🔴 デバッグ: データ長:', allProjectData.length);

        // デバッグ用アラート（一時的）
        // alert(`デバッグ: データ取得成功 ${allProjectData.length}件`);

        try {
          allResults = allProjectData.map((detail: any, index: number) => {
            // デバッグ: 各要素の処理を追跡
            if (index === 0) {
              console.log('map処理開始 - 最初の要素の構造:', {
                hasTestRunTestCase: !!detail.test_run_test_case,
                hasScriptTestRunResult: !!detail.script_test_run_result,
                testRunTestCaseKeys: detail.test_run_test_case ? Object.keys(detail.test_run_test_case) : [],
                scriptTestRunResultKeys: detail.script_test_run_result ? Object.keys(detail.script_test_run_result) : []
              });

              // script_test_run_resultのvalueの中身を確認
              if (detail.script_test_run_result?.value) {
                let parsedValue = detail.script_test_run_result.value;
                if (typeof parsedValue === 'string') {
                  try {
                    parsedValue = JSON.parse(parsedValue);
                  } catch (e) {
                    console.error('value解析エラー:', e);
                  }
                }
                console.log('script_test_run_result.valueの構造:', {
                  type: typeof parsedValue,
                  hasTagAnalysis: !!parsedValue?.tag_analysis,
                  hasHumanJudgmentConfirmed: parsedValue?.tag_analysis?.human_judgment_confirmed,
                  keys: parsedValue ? Object.keys(parsedValue).slice(0, 10) : []
                });
              }
            }

            // テストケース情報を value から抽出
            let testCaseValue: any = {};
            try {
              testCaseValue = detail.test_run_test_case?.value ?
                (typeof detail.test_run_test_case.value === 'string' ?
                  JSON.parse(detail.test_run_test_case.value) :
                  detail.test_run_test_case.value) : {};
            } catch (error) {
              console.warn(`テストケース情報の解析エラー (index: ${index}):`, error);
            }
        
        // テスト結果のvalueをパース
        let resultValue = {};
        try {
          resultValue = detail.script_test_run_result.value ?
            (typeof detail.script_test_run_result.value === 'string' ?
              JSON.parse(detail.script_test_run_result.value) :
              detail.script_test_run_result.value) : {};
        } catch (error) {
          // テスト結果の解析エラー
        }
        
        return {
          id: detail.script_test_run_result.result_id,
          test_result_status_id: detail.script_test_run_result.test_result_status_id,
          value: resultValue,
          test_case_id: detail.test_run_test_case.testcase_id,
          test_case: {
            id: detail.test_run_test_case.testcase_id,
            primary_item: testCaseValue.primary_item || '',
            secondary_item: testCaseValue.secondary_item || '',
            tertiary_item: testCaseValue.tertiary_item || '',
            value: testCaseValue  // valueを追加して論理名を取得できるようにする
            }
          };
        });
          console.log('allResults作成完了:', allResults.length, '件');
          console.log('allResults[0]:', allResults[0]);
        } catch (mapError) {
          console.error('map処理中にエラーが発生:', mapError);
          throw mapError;
        }
      } else if (apiClient) {
        // キャッシュがない場合はAPIから取得（ページング処理）
        console.log('APIからデータを取得');
        
        let allApiResults: any[] = [];
        let page = 1;
        const pageSize = 1000;
        let hasMore = true;
        
        // ページング処理で全件取得
        while (hasMore) {
          const findRequest = {
            page: page,
            size: pageSize,
            workspace_id: workspaceId,
            project_id: projectId,
            query: { and: [] }
          };
          
          const response = await apiClient.findTestResults(findRequest);
          
          if (response?.script_test_result_details && response.script_test_result_details.length > 0) {
            allApiResults = allApiResults.concat(response.script_test_result_details);
            
            // 取得したデータがpageSizeより少ない場合は最後のページ
            if (response.script_test_result_details.length < pageSize) {
              hasMore = false;
            } else {
              page++;
              console.log(`ページ${page}を読み込み中...`);
            }
          } else {
            hasMore = false;
          }
        }
        
        console.log(`全${allApiResults.length}件のデータを取得しました`);

        // 取得したデータをキャッシュに保存するためコールバックを呼び出す
        if (onDataCached) {
          onDataCached(allApiResults);
        }

        allResults = allApiResults.map((detail: any) => {
          // テストケース情報を value から抽出
          let testCaseValue: any = {};
          try {
            testCaseValue = detail.test_run_test_case.value ? 
              (typeof detail.test_run_test_case.value === 'string' ? 
                JSON.parse(detail.test_run_test_case.value) : 
                detail.test_run_test_case.value) : {};
          } catch (error) {
            // テストケース情報の解析エラー
          }
          
          // テスト結果のvalueをパース
          let resultValue = {};
          try {
            resultValue = detail.script_test_run_result.value ?
              (typeof detail.script_test_run_result.value === 'string' ?
                JSON.parse(detail.script_test_run_result.value) :
                detail.script_test_run_result.value) : {};
          } catch (error) {
            // テスト結果の解析エラー
          }
          
          return {
            id: detail.script_test_run_result.result_id,
            test_result_status_id: detail.script_test_run_result.test_result_status_id,
            value: resultValue,
            test_case_id: detail.test_run_test_case.testcase_id,
            test_case: {
              id: detail.test_run_test_case.testcase_id,
              primary_item: testCaseValue.primary_item || '',
              secondary_item: testCaseValue.secondary_item || '',
              tertiary_item: testCaseValue.tertiary_item || '',
              value: testCaseValue
            }
          };
        });
      } else {
        alert('データを取得できません');
        setIsLoading(false);
        return;
      }
      
      // TagAnalysisServiceを使用してタグ統計を計算
      const tagService = new TagAnalysisService();

      // デバッグ：最初のデータ構造を確認
      if (allResults.length > 0) {
        console.log('タグ統計計算用データサンプル:', {
          firstResult: allResults[0],
          valueType: typeof allResults[0]?.value,
          hasTagAnalysis: !!allResults[0]?.value?.tag_analysis,
          hasSteps: !!allResults[0]?.value?.tag_analysis?.steps,
          stepsLength: allResults[0]?.value?.tag_analysis?.steps?.length
        });
      }

      // すべてのテスト結果から人間判定済みのものを抽出
      const confirmedResults = allResults.filter((result: any) => {
        let value = result.value;
        // valueがJSON文字列の場合はパース
        if (typeof value === 'string') {
          try {
            value = JSON.parse(value);
          } catch (e) {
            return false;
          }
        }
        // tag_analysis.human_judgment_confirmedまたはhuman_judgment_confirmedを確認
        const isConfirmed = value?.tag_analysis?.human_judgment_confirmed === true ||
                           value?.human_judgment_confirmed === true;
        return isConfirmed;
      });

      console.log(`人間判定済み結果: ${confirmedResults.length}/${allResults.length}件`);

      // confirmedResultsが0の場合、全てのテスト結果を使用
      const resultsToProcess = confirmedResults.length > 0 ? confirmedResults : allResults;

      // アクションタグのみを収集（primary_item, secondary_item, tertiary_itemは廃止）
      const allActionTags = new Set<string>();

      // ステップレベルのタグを収集（新旧両方のデータ形式に対応）
      // 判定済みの結果のみから収集（統計と一致させるため）
      const dataForTagCollection = confirmedResults.length > 0 ? confirmedResults : allResults;
      
      // アクションタグと論理名のマッピングを収集
      const actionTagToLogicalNames = new Map<string, Set<string>>();
      
      dataForTagCollection.forEach((result: any, idx: number) => {
        // valueがJSON文字列の場合はパース
        let value = result.value;
        if (typeof value === 'string') {
          try {
            value = JSON.parse(value);
          } catch (e) {
            return;
          }
        }
        
        // テストケースのvalueから論理名を取得。
        // 結果 value 内の testcase_snapshot を優先し、無ければ live のテストケース value へ
        // フォールバックする（凍結廃止後も snapshot から復元されるようにする / #886）。
        let testCaseValue: any = {};
        try {
          const matchedResult = allResults.find((r: any) => r.id === result.id);
          testCaseValue = resolveTestCaseValue(
            value,
            () => matchedResult?.test_case?.value ?? {},
            "TagInfoButton"
          );
        } catch (e) {
          // 論理名取得エラー
        }
        
        // 新しい形式（tag_analysis.steps）の処理
        if (value?.tag_analysis?.steps) {
          const steps = value.tag_analysis.steps;
          steps.forEach((step: any, stepIdx: number) => {
            // アクションタグのみを収集（primary_item, secondary_item, tertiary_itemは廃止）
            const actionTags = step.evaluated_tags?.action_tags || [];
            actionTags.forEach((tag: string) => {
              if (tag) {
                allActionTags.add(tag);

                // 対応するステップの論理名を取得
                if (testCaseValue?.teststep && testCaseValue.teststep[stepIdx]) {
                  const testStep = testCaseValue.teststep[stepIdx];
                  const actionDetail = testStep.action_detail;
                  if (actionDetail?.test_targets) {
                    actionDetail.test_targets.forEach((target: any) => {
                      if (target.name) {
                        if (!actionTagToLogicalNames.has(tag)) {
                          actionTagToLogicalNames.set(tag, new Set());
                        }
                        actionTagToLogicalNames.get(tag)?.add(target.name);
                      }
                    });
                  }
                }
              }
            });
          });
        }

        // 古い形式（test_step_result）の処理 - タグ情報がない場合はスキップ
        else if (value?.test_step_result && Array.isArray(value.test_step_result)) {
          // 古い形式ではタグ情報がないため、処理をスキップ
        }
      });

      // アクションタグのみを使用（primary_item, secondary_item, tertiary_itemは廃止）
      const actionTagsArray = Array.from(allActionTags);

      // ステップレベルのタグ統計のみ計算（action_tagsのみ）
      const stepLevelStats = tagService.calculateStepLevelTagNgRates(
        allResults as any,  // 型の互換性の問題を一時的に回避
        actionTagsArray
      );


      // 統計情報を整形（action_tagsのみ）
      const formattedStats = {
        summary: {
          totalResults: allResults.length,
          confirmedResults: confirmedResults.length,
          totalTags: actionTagsArray.length,
          uniqueTags: {
            action: actionTagsArray.length
          }
        },
        stepLevel: stepLevelStats,
        actionTagLogicalNames: Object.fromEntries(
          Array.from(actionTagToLogicalNames.entries()).map(([tag, names]) =>
            [tag, Array.from(names).join(', ')]
          )
        )
      };

      setTagStatistics(formattedStats);
      setAllTestResults(allResults);
      setShowModal(true);
    } catch (error) {
      console.error('タグ統計の取得に失敗しました:', error);
      console.error('エラーの詳細:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      alert('タグ統計の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTagStats = (stats: any, tagType: string) => {
    if (!stats) {
      return [];
    }
    
    const entries = Object.entries(stats)
      .filter(([key]) => key.startsWith(`${tagType}:`))
      .map(([key, value]: [string, any]) => ({
        tag: key.replace(`${tagType}:`, ''),
        ...value
      }))
      .sort((a: any, b: any) => b.ng_rate - a.ng_rate);

    return entries;
  };

  const handleTagClick = (tagName: string, tagType: string) => {
    setSelectedTag({ name: tagName, type: tagType });
    setShowDetailModal(true);
  };

  const handleNavigateToCase = (resultId: string, stepIndex: number) => {
    console.log('ナビゲート先:', { resultId, stepIndex });
    
    // メインのモーダルを閉じる
    setShowModal(false);
    setShowDetailModal(false);
    
    // 選択されたテスト結果の完全なデータを取得
    const selectedFullData = allTestResults.find(result => result.id === resultId);
    
    // App.tsxにナビゲーションイベントを発火
    // 全データも一緒に渡す
    window.dispatchEvent(new CustomEvent('navigateToTestResult', {
      detail: { 
        resultId, 
        stepIndex,
        fullTestData: selectedFullData,
        allTestResults: allTestResults  // 全データを渡す
      }
    }));
  };

  const formatActionStats = (stats: any) => {
    if (!stats) {
      return [];
    }
    
    // アクションタグは "goto:", "click:", "change:" などのパターンを持つ
    // 基本タグは "primary_item:", "secondary_item:", "tertiary_item:" のパターンを持つ
    const entries = Object.entries(stats)
      .filter(([key]) => {
        // 基本タグのパターンを除外
        return !key.startsWith('primary_item:') && 
               !key.startsWith('secondary_item:') && 
               !key.startsWith('tertiary_item:');
      })
      .map(([key, value]: [string, any]) => ({
        tag: key,
        ...value
      }))
      .sort((a: any, b: any) => b.ng_rate - a.ng_rate);
    
    return entries;
  };

  return (
    <>
      <ButtonContainer>
        <InfoButton
          onClick={handleClick}
          isLoading={isLoading}
          disabled={isLoading || !workspaceId || !projectId}
          title={!workspaceId || !projectId ? 'テスト結果を検索してから実行してください' : 'タグ情報を確認'}
        >
          {isLoading && <Spinner />}
          タグ情報確認
        </InfoButton>
      </ButtonContainer>

      <Modal isOpen={showModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>タグ情報統計</ModalTitle>
            <CloseButton onClick={() => setShowModal(false)}>&times;</CloseButton>
          </ModalHeader>

          {tagStatistics ? (
            <>
              <SummarySection>
                <SummaryItem>
                  <SummaryLabel>全テスト結果:</SummaryLabel>
                  <SummaryValue>{tagStatistics.summary.totalResults}件</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>判定済み:</SummaryLabel>
                  <SummaryValue>{tagStatistics.summary.confirmedResults}件</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>アクションタグ:</SummaryLabel>
                  <SummaryValue>{tagStatistics.summary.uniqueTags.action}個</SummaryValue>
                </SummaryItem>
              </SummarySection>

              <TagTypeSection>
                <TagTypeTitle>アクションタグ統計</TagTypeTitle>

                {!tagStatistics.stepLevel || Object.keys(tagStatistics.stepLevel).length === 0 ? (
                  <p>タグ統計データがありません。タグ情報が設定されているテスト結果を確認してください。</p>
                ) : (
                  <>
                {(() => {
                  const actionStats = formatActionStats(tagStatistics.stepLevel);
                  if (actionStats.length === 0) {
                    return <p>アクションタグのデータがありません。</p>;
                  }

                  return (
                    <TagTable>
                      <TagTableHeader>
                        <tr>
                          <TagTableHeaderCell>タグ名</TagTableHeaderCell>
                          <TagTableHeaderCell>確認数</TagTableHeaderCell>
                          <TagTableHeaderCell>NG数</TagTableHeaderCell>
                          <TagTableHeaderCell>NG率</TagTableHeaderCell>
                        </tr>
                      </TagTableHeader>
                      <tbody>
                        {actionStats.map((stat: any) => (
                          <TagTableRow key={stat.tag}>
                            <TagTableCell
                              isClickable
                              onClick={() => handleTagClick(stat.tag, 'action')}
                            >
                              {stat.tag}
                            </TagTableCell>
                            <TagTableCell>{stat.total}</TagTableCell>
                            <TagTableCell>{stat.ng_count}</TagTableCell>
                            <TagTableCell>
                              <NGRateBadge rate={stat.ng_rate}>
                                {(stat.ng_rate * 100).toFixed(1)}%
                              </NGRateBadge>
                            </TagTableCell>
                          </TagTableRow>
                        ))}
                      </tbody>
                    </TagTable>
                  );
                })()}
                  </>
                )}
              </TagTypeSection>
            </>
          ) : (
            <div>
              <p>タグ統計データがありません。</p>
            </div>
          )}
        </ModalContent>
      </Modal>

      {selectedTag && (
        <TagDetailModal
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          tagName={selectedTag.name}
          tagType={selectedTag.type}
          allResults={allTestResults}
          onNavigateToCase={handleNavigateToCase}
        />
      )}
    </>
  );
};

export default TagInfoButton;